/*
 * COPYRIGHT MOTOROLA SOLUTIONS, INC.
 * ALL RIGHTS RESERVED.
 * MOTOROLA SOLUTIONS CONFIDENTIAL RESTRICTED
 */

import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { tap, withLatestFrom } from 'rxjs/operators';
import { PendoAnalyticsService } from './pendo-analytics.service';
import {
    CallActionType,
    CallDirection,
    PendoEventType,
    TriggerType
} from './models/pendo-events.types';
import {
    acdStatusChangeSuccess,
    acdAnswerRequestSuccess,
    acdAutoAnswer,
    updateCurrentRole,
    updateCurrentRoleSuccess
} from '../../user/+state/user.actions';
import {
    answerSuccess,
    requestReleaseCallSuccess,
    dialSuccess
} from '../../call/+state/call.actions';
import { selectRequestedAgentStatus, selectCurrentRole } from '../../user/+state/user.selectors';
import { selectCallsMap } from '../../call/+state/call.selectors';
import { Call } from 'CalltakingCoreApi';

@Injectable()
export class PendoAnalyticsEffects {
    private pendingRoleChange: { previousRole: string; newRole: string } | undefined;

    constructor(
        private readonly actions$: Actions,
        private readonly pendoAnalyticsService: PendoAnalyticsService,
        private readonly store: Store
    ) {}

    readonly trackAnswerCall$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(answerSuccess, acdAnswerRequestSuccess),
                concatLatestFrom(() => this.store.select(selectCallsMap)),
                tap(([action, callsMap]) => {
                    const call = callsMap[action.callId];
                    this.pendoAnalyticsService.track(PendoEventType.CallAction, {
                        trigger_type: TriggerType.Manual,
                        action: CallActionType.Answer,
                        call_id: action.callId,
                        call_direction: this.getCallDirection(call)
                    });
                })
            ),
        { dispatch: false }
    );

    readonly trackAutoAnswerCall$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(acdAutoAnswer),
                concatLatestFrom(() => this.store.select(selectCallsMap)),
                tap(([action, callsMap]) => {
                    const callId = (action.bid as { callId?: string }).callId;
                    const call = callId ? callsMap[callId] : undefined;
                    this.pendoAnalyticsService.track(PendoEventType.CallAction, {
                        trigger_type: TriggerType.Automatic,
                        action: CallActionType.Answer,
                        call_id: callId,
                        call_direction: this.getCallDirection(call)
                    });
                })
            ),
        { dispatch: false }
    );

    readonly trackReleaseCall$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(requestReleaseCallSuccess),
                concatLatestFrom(() => this.store.select(selectCallsMap)),
                tap(([action, callsMap]) => {
                    const call = callsMap[action.callId];
                    this.pendoAnalyticsService.track(PendoEventType.CallAction, {
                        trigger_type: TriggerType.Manual,
                        action: CallActionType.Release,
                        call_id: action.callId,
                        call_direction: this.getCallDirection(call)
                    });
                })
            ),
        { dispatch: false }
    );

    readonly trackInitiateCall$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(dialSuccess),
                tap((action) => {
                    // Initiated calls are always outbound by definition
                    this.pendoAnalyticsService.track(PendoEventType.CallAction, {
                        trigger_type: TriggerType.Manual,
                        action: CallActionType.Initiate,
                        call_id: action.callId,
                        call_direction: CallDirection.Outbound
                    });
                })
            ),
        { dispatch: false }
    );

    readonly trackAcdStatus$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(acdStatusChangeSuccess),
                concatLatestFrom(() => this.store.select(selectRequestedAgentStatus)),
                tap(([, requestedStatus]) => {
                    const isManual = requestedStatus?.isAgentRequested ?? false;
                    const acdStatus = String(requestedStatus?.status ?? 'unknown');

                    this.pendoAnalyticsService.track(PendoEventType.AcdStatusChange, {
                        trigger_type: isManual ? TriggerType.Manual : TriggerType.Automatic,
                        acd_status: acdStatus,
                        reason: requestedStatus?.statusReasonDisplayName ?? 'unknown'
                    });
                })
            ),
        { dispatch: false }
    );

    // Capture both previous (from store) and new role (from action) before change
    readonly captureRoleBeforeChange$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(updateCurrentRole),
                withLatestFrom(this.store.select(selectCurrentRole)),
                tap(([action, currentRole]) => {
                    this.pendingRoleChange = {
                        previousRole: currentRole ?? 'unknown',
                        newRole: action.role
                    };
                })
            ),
        { dispatch: false }
    );

    // Track role change after success using captured values
    readonly trackRoleChangeSuccess$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(updateCurrentRoleSuccess),
                tap(() => {
                    if (this.pendingRoleChange) {
                        this.pendoAnalyticsService.track(PendoEventType.RoleChange, {
                            trigger_type: TriggerType.Manual,
                            previous_role: this.pendingRoleChange.previousRole,
                            new_role: this.pendingRoleChange.newRole
                        });
                        this.pendingRoleChange = undefined;
                    }
                })
            ),
        { dispatch: false }
    );

    /**
     * Determines the call direction for Pendo tracking.
     * Conference is detected by presence of conferenceSnapshot.
     */
    private getCallDirection(call: Call | undefined): CallDirection {
        if (!call) {
            return CallDirection.Unknown;
        }

        // Conference call has conferenceSnapshot with participants
        if (call.conferenceSnapshot?.participants?.length) {
            return CallDirection.Conference;
        }

        switch (call.type) {
            case 'INBOUND':
                return CallDirection.Inbound;
            case 'OUTBOUND':
                return CallDirection.Outbound;
            case 'INTERNAL':
                return CallDirection.Internal;
            default:
                return CallDirection.Unknown;
        }
    }
}
